/**
 * ValidationSchemas - Zod schemas for validating form inputs
 *
 * This file contains Zod schemas for validating various inputs in the signup flow.
 * Error messages use the i18n system for localization.
 */
import { z } from "zod";
import i18n from "./i18n";
/**
 * Helper to get localized validation error
 */
const getValidationError = (key) => {
    return i18n.t(`validation.${key}`);
};
/**
 * Function to create phone number validation schema with current locale
 * Validates that:
 * - Starts with + and country code
 * - Contains between 10-15 digits total
 */
export const createPhoneNumberSchema = () => {
    return z
        .string()
        .refine((value) => /^\+\d{1,3}/.test(value), {
        message: getValidationError("phone.invalid"),
    })
        .refine((value) => {
        const digitsOnly = value.replace(/\D/g, "");
        return digitsOnly.length >= 10 && digitsOnly.length <= 15;
    }, { message: getValidationError("phone.invalid") });
};
/**
 * Function to create email validation schema with current locale
 * Uses Zod's built-in email validator
 */
export const createEmailSchema = () => {
    return z.string().email({ message: getValidationError("email.invalid") });
};
/**
 * Function to create verification code validation schema with current locale
 * Validates that:
 * - Code is exactly 5 characters long
 * - Code contains only digits
 */
export const createVerificationCodeSchema = () => {
    return z
        .string()
        .length(5, { message: getValidationError("verification.invalid") })
        .regex(/^\d+$/, { message: getValidationError("verification.invalid") });
};
// For backward compatibility, export instances with the current locale
export const phoneNumberSchema = createPhoneNumberSchema();
export const emailSchema = createEmailSchema();
export const verificationCodeSchema = createVerificationCodeSchema();
